// src/hooks/useHelp.js
// Filters help articles and categories based on the current user's features,
// billing mode, and permissions. Provides search and lookup utilities.

import { useMemo, useCallback } from 'react';
import helpIndex from '@/data/helpIndex.json';
import { useFeatureFlags } from '@/contexts/FeatureContext';
import { useUser } from '@/contexts/UserContext';
import { usePermissions } from '@/contexts/PermissionContext';

/**
 * Check if an article (or category) is visible given the current user context.
 */
function isVisible(item, solutions, billingMode, permissionChecker) {
  // 1. Feature check
  const features = item.requiredFeatures || [];
  if (features.length > 0) {
    const requireAny = item.requireAny !== false; // default true
    const featureMatch = requireAny
      ? features.some(f => solutions[f])
      : features.every(f => solutions[f]);
    if (!featureMatch) return false;
  }

  // 2. Billing mode check (articles only)
  const billingModes = item.requiredBillingMode || [];
  if (billingModes.length > 0) {
    if (!billingModes.includes(billingMode)) return false;
  }

  // 3. Permission check (articles only)
  const perms = item.requiredPermissions || [];
  if (perms.length > 0) {
    if (!permissionChecker.canAll(perms)) return false;
  }

  return true;
}

export function useHelp() {
  const { solutions } = useFeatureFlags();
  const { user } = useUser();
  const permissionChecker = usePermissions();

  const billingMode = user?.organization?.settings?.billing_mode || 'cash';

  // Filter articles
  const articles = useMemo(() => {
    if (!helpIndex?.articles) return [];
    return helpIndex.articles.filter(article =>
      isVisible(article, solutions, billingMode, permissionChecker)
    );
  }, [solutions, billingMode, permissionChecker]);

  // Filter categories — only show categories that have visible articles
  // or pass their own feature requirements
  const categories = useMemo(() => {
    if (!helpIndex?.categories) return [];

    const articleCategories = new Set(articles.map(a => a.category));

    return helpIndex.categories
      .filter(cat => {
        // Category must pass its own feature gate
        if (!isVisible(cat, solutions, billingMode, permissionChecker)) return false;
        // And must have at least one visible article
        return articleCategories.has(cat.key);
      })
      .sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [articles, solutions, billingMode, permissionChecker]);

  // Lookup a single article by category + slug
  const getArticle = useCallback((category, slug) => {
    return articles.find(a => a.category === category && a.slug === slug) || null;
  }, [articles]);

  // Get all articles for a category
  const getArticlesByCategory = useCallback((categoryKey) => {
    return articles
      .filter(a => a.category === categoryKey)
      .sort((a, b) => a.order - b.order);
  }, [articles]);

  // Get articles relevant to a specific route
  const getArticlesForRoute = useCallback((routePath) => {
    return articles.filter(a =>
      a.relatedRoutes?.some(r => routePath.startsWith(r))
    );
  }, [articles]);

  // Text search across title, description, tags, and content
  const searchArticles = useCallback((query) => {
    if (!query || query.trim().length === 0) return [];

    const words = query.toLowerCase().split(/\s+/).filter(Boolean);
    if (words.length === 0) return [];

    const scored = articles.map(article => {
      const title = (article.title || '').toLowerCase();
      const description = (article.description || '').toLowerCase();
      const tags = (article.tags || []).join(' ').toLowerCase();
      const content = (article.content || '').toLowerCase();

      let score = 0;
      for (const word of words) {
        if (title.includes(word)) score += 3;
        if (tags.includes(word)) score += 2;
        if (description.includes(word)) score += 2;
        if (content.includes(word)) score += 1;
      }

      return { article, score };
    });

    return scored
      .filter(s => s.score > 0)
      .sort((a, b) => b.score - a.score)
      .map(s => s.article);
  }, [articles]);

  return {
    categories,
    articles,
    getArticle,
    getArticlesByCategory,
    getArticlesForRoute,
    searchArticles,
  };
}
